import { inject } from '@angular/core';
import { type CanActivateFn, Router } from '@angular/router';
import { from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AUTH_ERROR_ROUTE, DEFAULT_AUTH_REDIRECT } from '../constants/auth.constants';
import { SsoAuthService } from '../services/sso-auth.service';
import { isSafeRelativePath } from '../../shared/helpers/url.helpers';

export const ssoCallbackGuard: CanActivateFn = () => {
  const router = inject(Router);
  const ssoAuthService = inject(SsoAuthService);

  if (ssoAuthService.hasValidAccessToken()) {
    return of(resolvePostLoginRedirect(router, ssoAuthService));
  }

  return from(ssoAuthService.tryLogin()).pipe(
    map(loggedIn => {
      if (loggedIn) {
        return resolvePostLoginRedirect(router, ssoAuthService);
      }

      ssoAuthService.startLogin(resolveRetryRedirectPath(ssoAuthService));
      return false;
    }),
    catchError((error: unknown) => {
      console.error(error);
      return of(resolveAuthFailureRedirect(router, ssoAuthService));
    }),
  );
};

function resolveRetryRedirectPath(ssoAuthService: SsoAuthService): string {
  const desiredPath = ssoAuthService.getPostLoginRedirectPath();

  if (desiredPath && isSafeRelativePath(desiredPath)) {
    return desiredPath;
  }

  return DEFAULT_AUTH_REDIRECT;
}

function resolvePostLoginRedirect(router: Router, ssoAuthService: SsoAuthService) {
  const desiredPath = ssoAuthService.getPostLoginRedirectPath();

  if (desiredPath && isSafeRelativePath(desiredPath)) {
    return router.parseUrl(desiredPath);
  }

  return router.parseUrl(DEFAULT_AUTH_REDIRECT);
}

function resolveAuthFailureRedirect(router: Router, ssoAuthService: SsoAuthService) {
  const desiredPath = ssoAuthService.getPostLoginRedirectPath();
  const redirect =
    desiredPath && isSafeRelativePath(desiredPath) ? desiredPath : DEFAULT_AUTH_REDIRECT;

  return router.createUrlTree([AUTH_ERROR_ROUTE], {
    queryParams: { redirect },
  });
}
